import { z as zod } from "zod";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, type FC } from "react";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { useOrdersQuery } from "../../get";
import type { CreateOrderDTO } from "../lib/dto";
import { toaster } from "@/components/ui/toaster";
import { CreateOrderLineSchema } from "../lib/schema";
import { FormikFormContext } from "../lib/create-order-context";
import { useCreateOrderMutation } from "../lib/use-create-order-mutation";

import type { Company } from "@entities/company";
import type { Wrapper } from "@shared/types/misc";
import { useDataPage } from "@shared/lib/use-data-page";
import { useAuthenticationContext } from "@app/auth/use-authentication-context";
import {
  clearOrderDraft,
  readOrderDraft,
  writeOrderDraft,
  type DraftScopeInput,
} from "../lib/order-draft-storage";

export interface Form extends CreateOrderDTO {}
interface Props extends Wrapper {
  onCompleted?: () => void;
}

const DOC_NUMBER_LENGTH = 3;

const validationSchema = zod.object({
  docNumber: zod.string().nonempty(),
  lines: zod.array(CreateOrderLineSchema),
});

/**
 * Formik's `resetForm({ values })` also replaces `initialValues`, so a plain
 * reset afterwards cannot bring back the draft that seeded them.
 */
const emptyCart = (values: Form): Form => ({
  ...values,
  lines: [],
  note: "",
  POOrderNumber: "",
});

const CreateOrderFormProvider: FC<Props> = ({ children, onCompleted }) => {
  const { data } = useOrdersQuery({
    filters: { page: 1, pageSize: 1 },
  });
  const { totalPages, rows = [] } = useDataPage(data);

  const { user, activeCompany } = useAuthenticationContext();

  const scope: DraftScopeInput = {
    userId: Number(user?.id) || null,
    companyId: activeCompany?.id ?? null,
  };

  /**
   * Read once, before the first render, so the draft can seed `initialValues`
   * instead of racing the effects that reset the form.
   */
  const restoredDraft = useRef(readOrderDraft(scope)).current;

  const { mutateAsync } = useCreateOrderMutation({
    onSuccess: () => {
      clearOrderDraft();
      onCompleted?.();
      toaster.success({
        duration: 5000,
        title: "Order successfully created",
        description:
          "The manager will review your order and get back to you soon.",
      });
    },
  });

  const nextDocNumber = useMemo<string>(
    () =>
      `${(activeCompany as Company)?.prefix || ""}-${
        rows?.length
          ? (totalPages + 1).toString().padStart(DOC_NUMBER_LENGTH, "0")
          : "001"
      }`,
    [totalPages, activeCompany, rows]
  );

  const formik = useFormik<Form>({
    initialValues: {
      lines: restoredDraft?.lines ?? [],
      note: restoredDraft?.note ?? "",
      POOrderNumber: restoredDraft?.POOrderNumber ?? "",
      docNumber: `${(activeCompany as Company)?.prefix || ""}-${Date.now()}`,
    },
    onSubmit: (values, { resetForm }) =>
      mutateAsync(values).then(() =>
        resetForm({ values: emptyCart(values) }),
      ),
    validateOnChange: true,
    validationSchema: toFormikValidationSchema(validationSchema),
  });

  /**
   * Different companies have a different products and form autofill settings
   * so we reset the form to start fresh. Usually user sees the confirmation to approve
   * switching company when there are items in the cart
   *
   * The ref starts at the current company so the mount run is a no-op — it would
   * otherwise discard the draft just restored into `initialValues`.
   */
  const previousCompanyId = useRef(activeCompany?.id);

  useEffect(() => {
    if (previousCompanyId.current === activeCompany?.id) return;

    previousCompanyId.current = activeCompany?.id;
    clearOrderDraft();
    formik.resetForm({ values: emptyCart(formik.values) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  useEffect(() => {
    writeOrderDraft(
      {
        lines: formik.values.lines,
        note: formik.values.note,
        POOrderNumber: formik.values.POOrderNumber,
      },
      scope,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.lines,
    formik.values.note,
    formik.values.POOrderNumber,
    scope.userId,
    scope.companyId,
  ]);

  useEffect(() => {
    formik.setFieldValue("docNumber", nextDocNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextDocNumber]);

  return (
    <FormikFormContext.Provider value={{ ...formik }}>
      {children}
    </FormikFormContext.Provider>
  );
};

export { CreateOrderFormProvider };
